import { formatChartValue } from '@/components/ui/chart/utils/formatters'
import type { ChartSpec } from './types'

// How much horizontal room each kind of chart needs per item before it gets
// too cramped to read in the narrow chat panel. Tuned conservatively: only a
// genuinely dense chart collapses to a preview. Dial these after seeing real
// charts. Bigger number = needs more room per item = collapses sooner.
const SANKEY_PX_PER_NODE = 42
const NETWORK_PX_PER_NODE = 28
const BAR_PX_PER_POINT = 14

// A label longer than this many characters won't fit a sankey/network node
// gutter or a bar tick at the given width, so it's a sign the chart is busy
// even when the item count is modest.
function maxLabelChars(width: number): number {
  return Math.max(16, Math.round(width / 15))
}

function chartLabels(spec: ChartSpec): string[] {
  if (spec.type === 'sankey' || spec.type === 'network') {
    return spec.nodes.map((node) => node.name ?? '')
  }
  return (spec.data ?? []).map(
    (point) => point.formattedDate ?? point.date ?? '',
  )
}

// True when the chart is too dense to render legibly at `width` px inline, so
// it should show a click-to-expand preview instead of cramming the full chart
// into the panel. Width-driven, so the same chart renders inline once the
// panel is dragged wide enough. Returns false while width is still unknown
// (0) so we never flash a preview before the container has measured.
export function isChartBusy(spec: ChartSpec, width: number): boolean {
  if (width <= 0) return false

  const labelLimit = maxLabelChars(width)
  if (chartLabels(spec).some((label) => label.length > labelLimit)) return true

  switch (spec.type) {
    case 'sankey':
      return spec.nodes.length > width / SANKEY_PX_PER_NODE
    case 'network':
      return spec.nodes.length > width / NETWORK_PX_PER_NODE
    default:
      return (spec.data?.length ?? 0) > width / BAR_PX_PER_POINT
  }
}

// One-line headline shown under the preview thumbnail so the user knows what
// they're about to open without reading the (hidden) chart labels.
export function chartPreviewCaption(spec: ChartSpec): string {
  if (spec.type === 'sankey' || spec.type === 'network') {
    const total = spec.links.reduce((sum, link) => sum + (link.value || 0), 0)
    const formattedTotal = formatChartValue(
      total,
      spec.valueFormat ?? 'currency',
    )
    return `${spec.nodes.length} nodes · ${formattedTotal} total flow`
  }
  return `${spec.data?.length ?? 0} data points`
}
